import { captionSentence, coachVoiceColor } from '@goaltree/backend/convex/lib/coach'
import { type PipCorner, nearestPipCorner } from '@goaltree/backend/convex/lib/pipSnap'
import { cn } from '@goaltree/ui/lib/utils'
import { Mic, MicOff, Phone } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { CoachAvatar, useCoachFace } from '@/components/coach-avatar'
import {
  FaceVideo,
  type VoiceCall,
  fmtCallTime,
  reducedMotion,
  useCaptionsPref,
} from '@/components/coach-call'

const AVATAR = 72
const TAP_SLOP = 6

// Anchored with CSS so the safe areas, tab bar and FAB (phones, below lg) are
// cleared without measuring them; dragging is a transform on top of that.
const CORNER_CLASS: Record<PipCorner, string> = {
  'top-left': 'top-[calc(env(safe-area-inset-top,0px)+4.5rem)] left-3 lg:top-4 lg:left-6 flex-col',
  'top-right':
    'top-[calc(env(safe-area-inset-top,0px)+4.5rem)] right-3 lg:top-4 lg:right-6 flex-col items-end',
  'bottom-left':
    'bottom-[calc(env(safe-area-inset-bottom,0px)+8.25rem)] left-3 lg:bottom-6 lg:left-6 flex-col-reverse',
  'bottom-right':
    'bottom-[calc(env(safe-area-inset-bottom,0px)+8.25rem)] right-3 lg:bottom-6 lg:right-6 flex-col-reverse items-end',
}

type Drag = {
  id: number
  x0: number
  y0: number
  moved: boolean
  lastX: number
  lastY: number
  lastT: number
  vx: number
  vy: number
}

export function CoachCallPip({
  call,
  voice,
  name,
  corner,
  onCorner,
  onExpand,
  onHangUp,
}: {
  call: VoiceCall
  voice: string | undefined
  name: string
  corner: PipCorner
  onCorner: (corner: PipCorner) => void
  onExpand: () => void
  onHangUp: () => void
}) {
  const { t } = useTranslation()
  const color = coachVoiceColor(voice)
  const realistic = useCoachFace(voice)
  const [captions] = useCaptionsPref()
  const wrapRef = useRef<HTMLDivElement>(null)
  const ringRef = useRef<HTMLSpanElement>(null)
  const drag = useRef<Drag | null>(null)
  const dragged = useRef(false)
  const from = useRef<DOMRect | null>(null)
  const [settle, setSettle] = useState(0)
  const levelsRef = useRef(call.levels)
  levelsRef.current = call.levels
  const speaking = call.phase === 'speaking'
  const connecting = call.status === 'connecting'

  useEffect(() => {
    const ring = ringRef.current
    if (!ring || reducedMotion()) return
    let raf = 0
    let level = 0
    const frame = () => {
      const l = levelsRef.current()
      level = level * 0.7 + (speaking ? l.output : l.input) * 0.3
      ring.style.transform = `scale(${1 + level * 0.22})`
      ring.style.opacity = String(0.35 + level * 0.6)
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [speaking])

  // FLIP into the new corner: start where the drag let go, glide to the anchor.
  useLayoutEffect(() => {
    const el = wrapRef.current
    const start = from.current
    from.current = null
    if (!el || !start) return
    el.style.transition = ''
    el.style.transform = ''
    if (reducedMotion()) return
    const end = el.getBoundingClientRect()
    el.style.transform = `translate(${start.left - end.left}px, ${start.top - end.top}px)`
    void el.offsetWidth
    el.style.transition = 'transform 260ms cubic-bezier(0.2, 0.9, 0.3, 1.15)'
    el.style.transform = ''
  }, [corner, settle])

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    dragged.current = false
    if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const now = performance.now()
    drag.current = {
      id: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      moved: false,
      lastX: e.clientX,
      lastY: e.clientY,
      lastT: now,
      vx: 0,
      vy: 0,
    }
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const d = drag.current
    const el = wrapRef.current
    if (!d || d.id !== e.pointerId || !el) return
    const dx = e.clientX - d.x0
    const dy = e.clientY - d.y0
    if (!d.moved && Math.hypot(dx, dy) < TAP_SLOP) return
    d.moved = true
    const now = performance.now()
    const dt = Math.max(1, now - d.lastT) / 1000
    d.vx = (e.clientX - d.lastX) / dt
    d.vy = (e.clientY - d.lastY) / dt
    d.lastX = e.clientX
    d.lastY = e.clientY
    d.lastT = now
    el.style.transition = ''
    el.style.transform = `translate(${dx}px, ${dy}px)`
  }

  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    const d = drag.current
    const el = wrapRef.current
    if (!d || d.id !== e.pointerId) return
    drag.current = null
    if (!d.moved || !el) return
    dragged.current = true
    const card = el.firstElementChild?.getBoundingClientRect() ?? el.getBoundingClientRect()
    // A pause before letting go is a drop, not a fling.
    const still = performance.now() - d.lastT > 80
    const next = nearestPipCorner(
      { x: card.left, y: card.top },
      { width: window.innerWidth, height: window.innerHeight },
      { width: card.width, height: card.height },
      still ? undefined : { x: d.vx, y: d.vy },
    )
    from.current = el.getBoundingClientRect()
    if (next === corner) setSettle((n) => n + 1)
    else onCorner(next)
  }

  const sentence =
    call.lastSpeaker === 'coach' && call.coachCaption
      ? captionSentence(call.coachCaption.text, call.coachCaption.chars).text
      : null
  const caption = captions && !call.muted ? sentence : null
  const faceOn = !!realistic && call.faceShown

  return createPortal(
    <div
      ref={wrapRef}
      role="region"
      aria-label={t('callView.pip.region', { name })}
      className={cn('fixed z-[55] flex gap-2 touch-none select-none', CORNER_CLASS[corner])}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={(e) => {
        if (dragged.current || (e.target as HTMLElement).closest('button')) return
        onExpand()
      }}
    >
      <div className="flex w-[312px] items-center gap-2.5 rounded-2xl border border-border/70 bg-background/90 p-2.5 shadow-[0_12px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95">
        <div
          role="button"
          tabIndex={0}
          aria-label={t('callView.pip.expand', { name })}
          title={t('callView.pip.expand', { name })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onExpand()
            }
          }}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span className="relative shrink-0" style={{ width: AVATAR, height: AVATAR }}>
            <span
              ref={ringRef}
              aria-hidden
              className="pointer-events-none absolute -inset-1 rounded-full border-2"
              style={{ borderColor: speaking ? color : 'var(--primary)', opacity: 0.45 }}
            />
            {faceOn ? (
              <span className="absolute inset-0 overflow-hidden rounded-full bg-black">
                <img
                  src={realistic.image}
                  alt=""
                  className={cn(
                    'absolute inset-0 size-full object-cover',
                    call.faceLive && 'opacity-0',
                  )}
                />
                <FaceVideo
                  video={call.faceVideoRef.current}
                  className={cn('absolute inset-0', !call.faceLive && 'opacity-0')}
                />
              </span>
            ) : (
              <CoachAvatar voice={voice} size={AVATAR} speaking={speaking} />
            )}
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-[13px] font-bold text-foreground">{name}</span>
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              {connecting ? (
                t('callView.connecting')
              ) : (
                <>
                  <span className="size-1.5 rounded-full bg-red-500" />
                  <span className="font-mono tabular-nums">{fmtCallTime(call.elapsedSeconds)}</span>
                  {call.muted && <MicOff className="size-3" aria-hidden />}
                </>
              )}
            </span>
          </span>
        </div>
        <button
          type="button"
          onClick={call.toggleMute}
          aria-label={t(call.muted ? 'coach.unmute' : 'coach.mute')}
          aria-pressed={call.muted}
          title={t(call.muted ? 'coach.unmute' : 'coach.mute')}
          className={cn(
            'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors',
            call.muted
              ? 'bg-foreground text-background'
              : 'bg-muted text-foreground hover:bg-muted/70',
          )}
        >
          {call.muted ? <MicOff className="size-4" /> : <Mic className="size-4" />}
        </button>
        <button
          type="button"
          onClick={onHangUp}
          aria-label={t('coach.callEnd')}
          title={t('coach.callEnd')}
          className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600"
        >
          <Phone className="size-4 rotate-[135deg]" fill="currentColor" />
        </button>
      </div>
      {caption && (
        <p
          aria-live="polite"
          className="hidden w-[312px] rounded-xl bg-background/85 px-3 py-1.5 text-[12px] leading-snug font-medium text-foreground shadow-md backdrop-blur-md sm:line-clamp-2"
        >
          {caption}
        </p>
      )}
    </div>,
    document.body,
  )
}
